<template>
  <button
    v-if="canViewActivities"
    type="button"
    class="btn btn-outline-secondary btn-sm btn-action-icon"
    :title="titleText"
    :aria-label="titleText"
    @click="openDialog"
  >
    <i class="fa fa-clock-rotate-left"></i>
  </button>

  <PrimevueDialog
    v-model:visible="dialogVisible"
    :modal="true"
    :style="{ width: '760px', maxWidth: '96vw' }"
    :header="dialogTitle"
  >
    <div class="module-activity-dialog">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <small class="text-muted">Les 10 dernières modifications enregistrées pour ce module.</small>

        <button
          type="button"
          class="btn btn-outline-secondary btn-sm btn-icon-label"
          :disabled="isLoading"
          title="Actualiser"
          aria-label="Actualiser"
          @click="fetchActivities"
        >
          <i class="fa fa-rotate-right" :class="{ 'fa-spin': isLoading }"></i>
          <span class="btn-label ms-1">Actualiser</span>
        </button>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 mb-3">
        {{ errorMessage }}
      </div>

      <div v-if="isLoading" class="module-activity-list" aria-live="polite">
        <div
          v-for="index in 4"
          :key="`module-activity-skeleton-${index}`"
          class="module-activity-item skeleton"
        >
          <div class="placeholder-glow w-100">
            <span class="placeholder col-3 mb-2"></span>
            <span class="placeholder col-10 mb-2"></span>
            <span class="placeholder col-4"></span>
          </div>
        </div>
      </div>

      <div v-else-if="!activities.length" class="module-activity-empty">
        <i class="fa fa-clock-rotate-left"></i>
        <strong>Aucune modification récente</strong>
        <span>Les nouvelles créations, mises à jour ou suppressions apparaîtront ici.</span>
      </div>

      <div v-else class="module-activity-list">
        <article
          v-for="activity in activities"
          :key="activity.id"
          class="module-activity-item"
        >
          <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                <strong class="text-capitalize">{{ activity.module || moduleLabel }}</strong>
                <span class="badge" :class="methodClass(activity.method || activity.action)">
                  {{ activity.method || activity.action || 'Action' }}
                </span>
              </div>

              <p class="mb-1 module-activity-description">
                {{ activity.description || activity.path || 'Modification enregistrée.' }}
              </p>

              <small class="text-muted">
                <i class="fa fa-user me-1"></i>
                {{ activity.user?.name || activity.user?.username || 'Système' }}
              </small>
            </div>

            <small class="text-muted text-nowrap">
              <i class="fa fa-calendar-days me-1"></i>
              {{ formatDate(activity.created_at) }}
            </small>
          </div>
        </article>
      </div>
    </div>
  </PrimevueDialog>
</template>

<script>
import { isSuperAdminUser, isSupportUser } from '@/support/permissions';

export default {
  name: 'ModuleActivityButton',
  props: {
    module: {
      type: [String, Array],
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dialogVisible: false,
      isLoading: false,
      errorMessage: '',
      activities: [],
    };
  },
  computed: {
    canViewActivities() {
      const user = this.$store?.getters?.['auth/currentUser'];

      return isSuperAdminUser(user) || isSupportUser(user);
    },
    moduleQuery() {
      return Array.isArray(this.module) ? this.module.join(',') : this.module;
    },
    moduleLabel() {
      return this.label || (Array.isArray(this.module) ? this.module[0] : this.module);
    },
    dialogTitle() {
      return this.label ? `Activités récentes - ${this.label}` : 'Activités récentes';
    },
    titleText() {
      return this.label
        ? `Voir les dernières modifications du module ${this.label}`
        : 'Voir les dernières modifications du module';
    },
  },
  methods: {
    async openDialog() {
      this.dialogVisible = true;
      await this.fetchActivities();
    },
    async fetchActivities() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get('/activities', {
          params: {
            module: this.moduleQuery,
            method: 'POST,PUT,PATCH,DELETE',
            paginate: 1,
            per_page: 10,
            page: 1,
            sort_by: 'id',
            sort_dir: 'desc',
          },
        });

        this.activities = Array.isArray(response.data?.data) ? response.data.data : [];
      } catch (error) {
        console.error('Erreur activités module', error);
        this.activities = [];
        this.errorMessage = 'Impossible de charger les modifications récentes.';
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(value) {
      return value ? this.$format.datetime(value) : '-';
    },
    methodClass(method) {
      const value = String(method || '').toUpperCase();

      if (value === 'POST') return 'bg-success';
      if (['PUT', 'PATCH'].includes(value)) return 'bg-primary';
      if (value === 'DELETE') return 'bg-danger';

      return 'bg-secondary';
    },
  },
};
</script>

<style scoped>
.module-activity-list {
  display: grid;
  gap: 0.75rem;
}

.module-activity-item {
  background: var(--suite241-card-bg, #ffffff);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.85rem;
}

.module-activity-item.skeleton {
  min-height: 5.25rem;
}

.module-activity-description {
  color: #1f2937;
  font-size: 0.9rem;
  font-weight: 650;
}

.module-activity-empty {
  align-items: center;
  border: 1px dashed rgba(15, 23, 42, 0.18);
  border-radius: 0.75rem;
  color: #64748b;
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  padding: 2rem 1rem;
  text-align: center;
}

.module-activity-empty i {
  color: #94a3b8;
  font-size: 1.7rem;
}
</style>
